import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * State, not decoration. A badge here names what something IS — running,
 * queued, failed — so the variants are the product's states, tinted from each
 * state's own hue in both themes rather than from one grey.
 */
const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 rounded-[4px] border px-1.5 py-0.5 text-[11px] leading-[1.35] font-medium whitespace-nowrap [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "border-brand/25 bg-brand-soft text-brand",
        secondary: "border-rule bg-raised text-ink-2",
        destructive: "border-bad/25 bg-bad-soft text-bad",
        outline: "border-rule text-ink-2",
        ghost: "border-transparent text-ink-2",
        link: "border-transparent text-brand underline-offset-4 [a&]:hover:underline",
        run: "border-run/30 bg-run-soft text-run",
        ok: "border-ok/25 bg-ok-soft text-ok",
        warn: "border-warn/30 bg-warn-soft text-warn",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
